import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import {
  LayoutDashboard, Upload, FileText, Building2, Settings,
  LogOut, Layers, ChevronLeft, ChevronRight, ChevronDown, ScanLine,
  ClipboardCheck, Map, Info, Users, Moon, Sun, Box, RefreshCcw, TrendingUp,
  Wrench, ClipboardList
} from 'lucide-react';
import { useState } from 'react';
import NotificationBell from '@/components/NotificationBell';

const DRAWING_INTEL_ITEMS = [
  { to: '/templates', icon: Settings, label: 'Templates' },
  { to: '/upload', icon: Upload, label: 'New Job' },
  { to: '/jobs', icon: FileText, label: 'All Jobs' },
  { to: '/digitize/projects', icon: FileText, label: 'Digitize Projects' },
  { to: '/digitize/upload', icon: ScanLine, label: 'Quick Digitize' },
];

const DRAWING_INTEL_PATHS = DRAWING_INTEL_ITEMS.map(i => i.to);

export default function Layout({ children }) {
  const { user, logout, isAdmin } = useAuth();
  const { darkMode, toggleDarkMode } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [diOpen, setDiOpen] = useState(() => DRAWING_INTEL_PATHS.some(p => location.pathname.startsWith(p)));

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const diActive = DRAWING_INTEL_PATHS.some(p => location.pathname.startsWith(p));

  const linkClass = (isActive) =>
    `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-heading tracking-wide uppercase transition-all duration-200 ${
      isActive
        ? 'bg-sky-500 text-white shadow-sm'
        : 'text-slate-300 hover:bg-secondary hover:text-white'
    }`;

  const renderNavLink = (item) => (
    <NavLink
      key={item.to}
      to={item.to}
      data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
      className={({ isActive }) => linkClass(isActive)}
    >
      <item.icon className="w-4 h-4 shrink-0" />
      {!collapsed && <span>{item.label}</span>}
    </NavLink>
  );

  return (
    <div className="flex h-screen overflow-hidden" data-testid="app-layout">
      {/* Sidebar */}
      <aside
        className={`relative flex flex-col border-r border-border/50 transition-[width] duration-200 ${collapsed ? 'w-16' : 'w-56'}`}
        style={{ backgroundColor: '#0F172A' }}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="flex items-center justify-center h-16 px-3 border-b border-border/50">
          <NavLink to="/dashboard" className="flex items-center">
            <img
              src={collapsed ? "/images/fmiq-logo-light.png" : "/images/fmiq-one-logo.png"}
              alt="FMiQ One"
              className={`transition-all duration-200 ${collapsed ? 'w-8 h-8 object-contain' : 'max-h-10 w-auto'}`}
              data-testid="sidebar-logo"
            />
          </NavLink>
        </div>

        {/* Nav */}
        <nav className="flex-1 py-4 space-y-1 px-2 overflow-y-auto">
          {/* Dashboard */}
          {renderNavLink({ to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' })}

          {/* Modules section */}
          <div className="my-3 px-3">
            <div className="border-t border-border/40" />
            {!collapsed && (
              <p className="mt-2 text-[9px] font-heading uppercase tracking-widest text-muted-foreground">Modules</p>
            )}
          </div>

          {/* Build — collapsible group (formerly Drawing Intel) */}
          <button
            onClick={() => { if (collapsed) { setCollapsed(false); setDiOpen(true); } else { setDiOpen(!diOpen); } }}
            data-testid="nav-build"
            className={`flex items-center gap-3 w-full px-3 py-2.5 rounded-md text-sm font-heading tracking-wide uppercase transition-all duration-200 ${
              diActive && !diOpen
                ? 'bg-sky-500/20 text-sky-300'
                : diOpen
                  ? 'text-white bg-secondary/50'
                  : 'text-slate-300 hover:bg-secondary hover:text-white'
            }`}
          >
            <Layers className="w-4 h-4 shrink-0" />
            {!collapsed && (
              <>
                <span className="flex-1 text-left">Build</span>
                <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${diOpen ? 'rotate-0' : '-rotate-90'}`} />
              </>
            )}
          </button>

          {/* Drawing Intel children */}
          {diOpen && !collapsed && (
            <div className="ml-3 pl-3 border-l border-slate-700/60 space-y-0.5" data-testid="drawing-intel-items">
              {DRAWING_INTEL_ITEMS.map(item => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 rounded-md text-xs font-heading tracking-wide uppercase transition-all duration-200 ${
                      isActive
                        ? 'bg-sky-500 text-white shadow-sm'
                        : 'text-slate-400 hover:bg-secondary hover:text-white'
                    }`
                  }
                >
                  <item.icon className="w-3.5 h-3.5 shrink-0" />
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </div>
          )}

          {/* Collapsed mode: show child items as icon-only */}
          {diOpen && collapsed && (
            <div className="space-y-0.5">
              {DRAWING_INTEL_ITEMS.map(item => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  title={item.label}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                  className={({ isActive }) =>
                    `flex items-center justify-center px-3 py-2 rounded-md transition-all duration-200 ${
                      isActive ? 'bg-sky-500 text-white shadow-sm' : 'text-slate-400 hover:bg-secondary hover:text-white'
                    }`
                  }
                >
                  <item.icon className="w-4 h-4" />
                </NavLink>
              ))}
            </div>
          )}

          {renderNavLink({ to: '/closeout', icon: ClipboardCheck, label: 'Activate' })}
          {renderNavLink({ to: '/operate', icon: Wrench, label: 'Operate' })}
          {renderNavLink({ to: '/space', icon: Box, label: 'Space' })}
          {renderNavLink({ to: '/recover', icon: RefreshCcw, label: 'Recover' })}
          {renderNavLink({ to: '/predict', icon: TrendingUp, label: 'Predict' })}
          {renderNavLink({ to: '/assess', icon: ClipboardList, label: 'Assess' })}

          {/* Reference section */}
          <div className="my-3 px-3">
            <div className="border-t border-border/40" />
            {!collapsed && (
              <p className="mt-2 text-[9px] font-heading uppercase tracking-widest text-muted-foreground">Reference</p>
            )}
          </div>
          {renderNavLink({ to: '/process-map', icon: Map, label: 'Process Map' })}
          {renderNavLink({ to: '/about', icon: Info, label: 'About FMiQ' })}

          {/* Admin section */}
          {isAdmin && (
            <>
              <div className="my-3 px-3">
                <div className="border-t border-border/40" />
                {!collapsed && (
                  <p className="mt-2 text-[9px] font-heading uppercase tracking-widest text-muted-foreground">Admin</p>
                )}
              </div>
              {renderNavLink({ to: '/admin/users', icon: Users, label: 'User Management' })}
            </>
          )}
        </nav>

        {/* User section */}
        <div className="border-t border-border/50 p-3">
          {!collapsed && user && (
            <div className="mb-2 px-1">
              <p className="text-xs font-medium text-white truncate">{user.name}</p>
              <p className="text-[10px] font-mono text-slate-400 truncate">{user.email}</p>
              {user.role && (
                <span className={`inline-block mt-1 text-[8px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm border ${
                  user.role === 'admin' ? 'text-amber-400 bg-amber-950/30 border-amber-800/40' :
                  user.role === 'manager' ? 'text-cyan-400 bg-cyan-950/30 border-cyan-800/40' :
                  'text-slate-400 bg-secondary/30 border-slate-600/40'
                }`}>{user.role}</span>
              )}
            </div>
          )}

          {/* Dark mode toggle */}
          <button
            onClick={toggleDarkMode}
            data-testid="dark-mode-toggle"
            className="flex items-center gap-2 w-full px-3 py-2 rounded-sm text-xs font-heading uppercase tracking-wider text-slate-400 hover:text-white hover:bg-secondary transition-colors duration-150"
            title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {darkMode ? <Sun className="w-4 h-4 text-yellow-400" /> : <Moon className="w-4 h-4" />}
            {!collapsed && <span>{darkMode ? 'Light Mode' : 'Dark Mode'}</span>}
          </button>

          <NavLink
            to="/settings"
            data-testid="nav-settings"
            className={({ isActive }) =>
              `flex items-center gap-2 w-full px-3 py-2 rounded-sm text-xs font-heading uppercase tracking-wider transition-colors duration-150 ${
                isActive
                  ? 'text-sky-400 bg-sky-500/10'
                  : 'text-slate-400 hover:text-white hover:bg-secondary'
              }`
            }
          >
            <Settings className="w-4 h-4 shrink-0" />
            {!collapsed && <span>Settings</span>}
          </NavLink>
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="flex items-center gap-2 w-full px-3 py-2 rounded-sm text-xs font-heading uppercase tracking-wider text-slate-400 hover:text-rose-400 hover:bg-rose-950/20 transition-colors duration-150"
          >
            <LogOut className="w-4 h-4 shrink-0" />
            {!collapsed && <span>Logout</span>}
          </button>
        </div>

        {/* Collapse toggle */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="absolute -right-3 top-16 w-6 h-6 rounded-full bg-secondary border border-slate-600 flex items-center justify-center text-slate-400 hover:text-white hover:bg-slate-700 transition-colors z-10"
          data-testid="sidebar-toggle"
        >
          {collapsed ? <ChevronRight className="w-3 h-3" /> : <ChevronLeft className="w-3 h-3" />}
        </button>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto bg-background relative z-[1]">
        <div className="sticky top-0 z-20 flex items-center justify-end px-6 py-2 bg-background/80 backdrop-blur-sm border-b border-border/50">
          <NotificationBell />
        </div>
        {children}
      </main>
    </div>
  );
}
